import { useCallback, useMemo } from 'react'
import { floorplanImagesRootGuid, normalizeStoreyImageTransform } from '@constants'
import { filter, find, first, sortBy } from 'lodash-es'
import { v4 } from 'uuid'
import { useFloorplanStore, useModelStore } from '@editor/stores'
import useDeleteDocument from './useDeleteDocument'
import useFileMetadata from './useFileMetadata'
import useProjectFiles from './useProjectFiles'
import useUploadProjectFile from './useUploadProjectFile'

const useStoreyImages = () => {
  const storeyImageVisibilityIn2DByStorey = useFloorplanStore(
    state => state.storeyImageVisibilityIn2DByStorey,
  )
  const storeyImageVisibilityIn3DByStorey = useFloorplanStore(
    state => state.storeyImageVisibilityIn3DByStorey,
  )
  const toggleStoreyImageVisibilityIn2D = useFloorplanStore(
    state => state.toggleStoreyImageVisibilityIn2D,
  )
  const toggleStoreyImageVisibilityIn3D = useFloorplanStore(
    state => state.toggleStoreyImageVisibilityIn3D,
  )
  const availableStoreys = useModelStore(state => state.availableStoreys)

  const { data: storeyImages } = useProjectFiles<StoreyImageTransform>(floorplanImagesRootGuid)

  const { mutate: mutateSetStoreyImage, isLoading: isUploadingStoreyImage } =
    useUploadProjectFile(floorplanImagesRootGuid)
  const { mutate: mutateRemoveStoreyImage, isLoading: isRemovingStoreyImage } =
    useDeleteDocument(floorplanImagesRootGuid)
  const { mutate: mutateSetStoreyImageMetadata } = useFileMetadata<StoreyImageTransform>(
    floorplanImagesRootGuid,
    { optimisticUpdate: true, debounceDelay: 2000 },
  )

  const storeyImagesMap = useMemo(() => {
    const storeyMap: Record<
      string,
      StoreyImageTransform & { isVisible2D: boolean; isVisible3D: boolean; url: string }
    > = {}
    availableStoreys.forEach(storey => {
      const storeyFiles = filter(storeyImages?.files, file => file.name === storey)
      const sortedStoreyFiles = sortBy(storeyFiles, file => file.hierarchy_position)
      const highedstStoreyFile = first(sortedStoreyFiles)
      if (highedstStoreyFile) {
        storeyMap[storey] = {
          ...normalizeStoreyImageTransform(
            highedstStoreyFile.additional_data as StoreyImageTransform,
          ),
          isVisible2D: storeyImageVisibilityIn2DByStorey[storey] ?? true,
          isVisible3D: storeyImageVisibilityIn3DByStorey[storey] ?? false,
          url: highedstStoreyFile.url as string,
        }
      }
    })

    return storeyMap
  }, [
    availableStoreys,
    storeyImages?.files,
    storeyImageVisibilityIn2DByStorey,
    storeyImageVisibilityIn3DByStorey,
  ])

  const setStoreyImage = useCallback(
    (storey: string, file: File, image: StoreyImageTransform, pdfPage?: number) => {
      const data: FileItem<StoreyImageTransform> & { file: File; pdfPage?: number } = {
        file,
        name: storey,
        additional_data: image,
        guid: v4(),
        parent_guid: floorplanImagesRootGuid,
        hierarchy_position: 0,
        type: 'file',
        pdfPage,
      }

      mutateSetStoreyImage(data)
    },
    [mutateSetStoreyImage],
  )

  const removeStoreyImage = useCallback(
    (storey: string) => {
      const file = find(storeyImages?.files, file => file.name === storey)
      if (!file) return

      mutateRemoveStoreyImage(file.guid)
    },
    [mutateRemoveStoreyImage, storeyImages],
  )

  const updateStoreyImageTransform = useCallback(
    (storey: string, transform: Partial<StoreyImageTransform>) => {
      const file = find(storeyImages?.files, file => file.name === storey)
      if (!file) return

      const data: {
        name: string
        item_guid: string
      } & StoreyImageTransform = {
        name: storey,
        item_guid: file.guid,
        ...normalizeStoreyImageTransform(file.additional_data as StoreyImageTransform),
        ...transform,
        offsetsInPdfCm: true,
      }

      mutateSetStoreyImageMetadata(data)
    },
    [mutateSetStoreyImageMetadata, storeyImages],
  )

  return {
    storeyImages: storeyImagesMap,
    setStoreyImage,
    removeStoreyImage,
    updateStoreyImageTransform,
    toggleStoreyImageVisibilityIn2D,
    toggleStoreyImageVisibilityIn3D,
    isRemovingStoreyImage,
    isUploadingStoreyImage,
  }
}

export default useStoreyImages
